import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import { useModalDismiss } from "./useModalDismiss";

interface Props {
    categoryId: string;
    categoryName: string;
    onSave: () => void;
    onCancel: () => void;
    linkId?: string;
    initialTitle?: string;
    initialUrl?: string;
    initialDescription?: string;
}

export default function LinkForm({
    categoryId,
    categoryName,
    onSave,
    onCancel,
    linkId,
    initialTitle,
    initialUrl,
    initialDescription,
}: Props) {
    const [url, setUrl] = useState(initialUrl ?? "");
    const [title, setTitle] = useState(initialTitle ?? "");
    const [description, setDescription] = useState(initialDescription ?? "");
    const [saving, setSaving] = useState(false);
    const { show, dismiss, onExited } = useModalDismiss(onCancel, onSave);

    const isEdit = !!linkId;

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        const u = url.trim();
        const t = title.trim();
        if (!u) { toast.error("URL is required"); return; }
        if (!t) { toast.error("Title is required"); return; }
        setSaving(true);
        const payload = {
            categoryId,
            folderId: null,
            title: t,
            url: u,
            description: description.trim() || null,
            icon: null,
        };
        const req = isEdit
            ? axios.put(`/api/manuals/links/${linkId}`, payload)
            : axios.post("/api/manuals/links", payload);
        req
            .then(res => {
                if (res.data.success) {
                    toast.success(isEdit ? "Link updated" : "Link added");
                    dismiss("save");
                } else {
                    toast.error(res.data.message || (isEdit ? "Failed to update link" : "Failed to add link"));
                    setSaving(false);
                }
            })
            .catch(err => {
                toast.error(err.response?.data?.message || (isEdit ? "Failed to update link" : "Failed to add link"));
                setSaving(false);
            });
    }

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered backdropClassName="m-modal-backdrop">
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>
                        {isEdit ? "Edit link" : "Add a link"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: "#64748b", fontSize: 13, marginTop: -4, marginBottom: 16 }}>
                        Links show up in page lists and are searchable by title.
                    </p>
                    <div className="mb-3">
                        <label className="m-label">URL</label>
                        <input
                            type="url"
                            className="m-input"
                            value={url}
                            onChange={e => setUrl(e.target.value)}
                            placeholder="https://..."
                            maxLength={2048}
                            autoFocus
                        />
                    </div>
                    <div className="mb-3">
                        <label className="m-label">Title</label>
                        <input
                            type="text"
                            className="m-input"
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            placeholder="e.g. Company handbook"
                            maxLength={512}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="m-label">Description</label>
                        <textarea
                            className="m-input"
                            rows={3}
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="m-label">Category</label>
                        <input type="text" className="m-input" value={categoryName} disabled />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Cancel</button>
                    <button type="submit" className="m-btn m-btn-primary" disabled={saving}>
                        {saving ? (isEdit ? "Saving..." : "Adding...") : (isEdit ? "Save changes" : "Add link")}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
